import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import type { GeneratedOtpResponse } from '@/bindings/GeneratedOtpResponse'
import type { OtpRequestSummary } from '@/bindings/OtpRequestSummary'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import {
  type OtpStatus,
  PURPOSE_LABELS,
  type SentState,
  generatePath,
  otpRequestKeys,
  otpRequestListQuery,
  sentState,
} from '@/features/otp-requests/api'
import { ApiError, request } from '@/lib/api'
import { cx } from '@/lib/cx'
import { Pager } from '@/components/pager'
import { useT } from '@/lib/i18n/translate'

/**
 * The manual OTP channel (E5): any platform user generates a queued code and
 * texts it by hand. The plain code is shown once, in the panel at the top —
 * the row itself leaves the queue the moment the list refetches.
 */
export function OtpRequestListPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const { page, status } = useSearch({ from: '/platform-auth/otp-requests' })
  const list = useQuery(otpRequestListQuery(status, page))
  const [generated, setGenerated] = useState<GeneratedOtpResponse | null>(null)

  const generate = useMutation({
    mutationFn: (id: string) => request<GeneratedOtpResponse>(generatePath(id), { method: 'POST' }),
    onSuccess: setGenerated,
    // Success or a vanished row (404): either way the queue on screen is stale.
    onSettled: () => queryClient.invalidateQueries({ queryKey: otpRequestKeys.all }),
  })
  const gone = generate.error instanceof ApiError && generate.error.status === 404

  const total = list.data?.total ?? 0

  return (
    <Page width="lg" title={t('otp.title')} className="flex flex-col gap-4">
      <nav aria-label={t('otp.tabs')} className="flex gap-2">
        <Tab status="pending" current={status}>
          {t('otp.pending')}
        </Tab>
        <Tab status="sent" current={status}>
          {t('otp.sent')}
        </Tab>
      </nav>

      {generated && <CodePanel generated={generated} onDone={() => setGenerated(null)} />}
      {gone ? (
        <p role="alert" className="border-warning text-warning rounded-lg border px-3 py-2 text-sm">
          {t('otp.gone')}
        </p>
      ) : (
        <FormError error={generate.error} />
      )}

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t(status === 'pending' ? 'otp.emptyPending' : 'otp.emptySent')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((item) => (
              <li key={item.id}>
                {status === 'pending' ? (
                  <PendingRow
                    item={item}
                    generating={generate.isPending && generate.variables === item.id}
                    onGenerate={() => generate.mutate(item.id)}
                  />
                ) : (
                  <SentRow item={item} />
                )}
              </li>
            ))}
          </ul>
        ))}

      <Pager label={t('otp.pager')} page={page} total={total} link={(page) => ({ to: '/otp-requests', search: { page, status } })} />
    </Page>
  )
}

function Tab({ status, current, children }: { status: OtpStatus; current: OtpStatus; children: string }) {
  const active = status === current
  return (
    <Link
      to="/otp-requests"
      search={{ page: 1, status }}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'flex min-h-tap items-center rounded-lg border px-4 font-medium',
        active ? 'border-accent bg-accent-soft text-accent-ink' : 'border-border bg-surface hover:bg-accent-soft',
      )}
    >
      {children}
    </Link>
  )
}


/** The one time the plain code is visible; copy it into the text message. */
function CodePanel({ generated, onDone }: { generated: GeneratedOtpResponse; onDone: () => void }) {
  const t = useT()
  const [copied, setCopied] = useState(false)

  function copy() {
    // Clipboard needs a secure context and a user gesture; the code stays on screen regardless.
    navigator.clipboard
      ?.writeText(generated.code)
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }

  return (
    <Card role="status" className="border-accent flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('otp.codeTitle', { identifier: generated.identifier })}</h2>
      <p className="font-mono text-3xl tracking-[0.3em]">{generated.code}</p>
      <p className="text-muted text-sm">
        {t('otp.codeIntro', { expires: new Date(generated.expires_at).toLocaleString() })}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={copy}>{copied ? t('otp.copied') : t('otp.copy')}</Button>
        <Button variant="secondary" onClick={onDone}>
          {t('otp.done')}
        </Button>
      </div>
    </Card>
  )
}

function PendingRow({ item, generating, onGenerate }: { item: OtpRequestSummary; generating: boolean; onGenerate: () => void }) {
  const t = useT()
  return (
    <Card className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <RowInfo item={item}>{t('otp.requestedAt', { time: new Date(item.created_at).toLocaleString() })}</RowInfo>
      <Button
        variant="secondary"
        loading={generating}
        aria-label={t('otp.generateFor', { identifier: item.identifier })}
        onClick={onGenerate}
        className="shrink-0 self-start sm:self-auto"
      >
        {t('otp.generate')}
      </Button>
    </Card>
  )
}

const STATE_TONES: Record<SentState, 'muted' | 'warning' | 'success'> = {
  used: 'muted',
  expired: 'warning',
  active: 'success',
}

function SentRow({ item }: { item: OtpRequestSummary }) {
  const t = useT()
  const state = sentState(item)
  const time = new Date(item.generated_at ?? item.created_at).toLocaleString()
  return (
    <Card className="flex items-center gap-3">
      <RowInfo item={item} extra={<Badge tone={STATE_TONES[state]}>{t(`otp.state.${state}`)}</Badge>}>
        {item.generated_by ? t('otp.sentBy', { name: item.generated_by, time }) : t('otp.sentAt', { time })}
      </RowInfo>
    </Card>
  )
}

function RowInfo({ item, extra, children }: { item: OtpRequestSummary; extra?: ReactNode; children: string }) {
  const t = useT()
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="truncate font-mono font-medium">{item.identifier}</span>
        <Badge tone="muted">{t(PURPOSE_LABELS[item.purpose])}</Badge>
        {extra}
      </span>
      <span className="text-muted text-sm">{children}</span>
    </span>
  )
}
